import { type FC, useState, type MouseEvent } from "react";
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  List,
  ListItemButton,
  Paper,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import SpeedIcon from "@mui/icons-material/Speed";
import { playerFooterControlPadding } from "./playerFooterControlPadding";
import type { VideoEngineLevel } from "./useVideoEngine";
import { useTranslation } from "react-i18next";

const RATES = [0.5, 1, 1.25, 1.5, 2];

/** Badge shown next to the gear icon and in the dropdown, matching the
 * reference player's HD / HD+ / 4K resolution labels. */
function qualityBadge(height: number): string | null {
  if (height >= 2160) return "4K";
  if (height >= 1440) return "HD+";
  if (height >= 1080) return "HD";
  return null;
}

const dropdownPaperSx = {
  position: "absolute" as const,
  bottom: 60,
  right: -12,
  backgroundColor: "rgba(0,0,0,0.75)",
  overflow: "hidden",
};

export const QualityMenu: FC<{
  levels: VideoEngineLevel[];
  currentLevel: number;
  autoLevel: boolean;
  onSetLevel: (index: number) => void;
}> = ({ levels, currentLevel, autoLevel, onSetLevel }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (levels.length === 0) return null;

  const sortedLevels = [...levels].sort((a, b) => b.height - a.height);
  const current = !autoLevel
    ? sortedLevels.find((level) => level.index === currentLevel)
    : null;
  const currentBadge = current ? qualityBadge(current.height) : null;

  const select = (index: number) => {
    onSetLevel(index);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <IconButton
          size="large"
          onClick={(event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setOpen((value) => !value);
          }}
          sx={(theme) => ({
            color: "#fff",
            ...playerFooterControlPadding(theme),
          })}
          aria-label={t("shared.player.quality", { defaultValue: "Качество" })}
        >
          {currentBadge ? (
            <Badge badgeContent={currentBadge} color="primary">
              <TuneIcon />
            </Badge>
          ) : (
            <TuneIcon />
          )}
        </IconButton>
        {open && (
          <Paper sx={dropdownPaperSx}>
            <List disablePadding dense>
              {sortedLevels.map((level) => {
                const badge = qualityBadge(level.height);
                return (
                  <ListItemButton
                    key={level.index}
                    selected={!autoLevel && currentLevel === level.index}
                    onClick={() => select(level.index)}
                    sx={{ justifyContent: "center", color: "#fff" }}
                  >
                    {badge ? (
                      <Badge badgeContent={badge} color="primary">
                        {level.height}p
                      </Badge>
                    ) : (
                      `${level.height}p`
                    )}
                  </ListItemButton>
                );
              })}
              <ListItemButton
                selected={autoLevel}
                onClick={() => select(-1)}
                sx={{ justifyContent: "center", color: "#fff" }}
              >
                {t("shared.player.auto", { defaultValue: "Авто" })}
              </ListItemButton>
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export const SpeedMenu: FC<{
  playbackRate: number;
  onSetRate: (rate: number) => void;
}> = ({ playbackRate, onSetRate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const showBadge = playbackRate !== 1;

  const select = (rate: number) => {
    onSetRate(rate);
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <IconButton
          size="large"
          onClick={(event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setOpen((value) => !value);
          }}
          sx={(theme) => ({
            color: "#fff",
            ...playerFooterControlPadding(theme),
          })}
          aria-label={t("shared.player.speed", { defaultValue: "Скорость" })}
        >
          {showBadge ? (
            <Badge badgeContent={`${playbackRate}x`} color="primary">
              <SpeedIcon />
            </Badge>
          ) : (
            <SpeedIcon />
          )}
        </IconButton>
        {open && (
          <Paper sx={{ ...dropdownPaperSx, minWidth: 80 }}>
            <List disablePadding dense>
              {RATES.map((rate) => (
                <ListItemButton
                  key={rate}
                  selected={playbackRate === rate}
                  onClick={() => select(rate)}
                  sx={{ justifyContent: "center", color: "#fff" }}
                >
                  {rate === 1
                    ? t("shared.player.normalSpeed", {
                        defaultValue: "Обычная",
                      })
                    : `${rate}x`}
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
